import React, { useState } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { useGetTicketWorkflowSlReportQuery } from "../../../../Services/reports/tickets/ReportTickets";
import TicketSlReportShell from "./TicketSlReportShell";
import {
  MenuProps,
  toggleMultiSelect,
  WORKFLOW_STATUS_OPTIONS,
  WORKFLOW_STATUS_VALUES,
} from "./ticketSlReportUtils";

const TicketWorkflowSlReport = () => {
  const [params, setParams] = useState<Record<string, string | number> | null>(
    null,
  );
  const [selectedWorkflowStatuses, setSelectedWorkflowStatuses] = useState<
    string[]
  >([]);

  const {
    data: reportData,
    isLoading: reportLoading,
    isFetching: reportFetching,
    isError,
    error,
    refetch,
  } = useGetTicketWorkflowSlReportQuery(params, { skip: !params });

  const isAllWorkflowStatusesSelected =
    selectedWorkflowStatuses.length === WORKFLOW_STATUS_OPTIONS.length;

  const buildExtraParams = () => {
    const effectiveValues =
      selectedWorkflowStatuses.length === 0 || isAllWorkflowStatusesSelected
        ? WORKFLOW_STATUS_VALUES
        : selectedWorkflowStatuses;

    const labels = effectiveValues.map(
      (value) =>
        WORKFLOW_STATUS_OPTIONS.find((option) => option.value === value)
          ?.label ?? value,
    );

    if (labels.length === 1) {
      return { workflow_status: labels[0] };
    }
    return { workflow_statuses: labels.join(",") };
  };

  const extraFilters = ({ selectedWorkflowId }: { selectedWorkflowId: string }) => (
    <div className="w-100 mw-200px">
      <FormControl fullWidth>
        <InputLabel id="workflow-status-label">Workflow Status</InputLabel>
        <Select
          labelId="workflow-status-label"
          multiple
          value={selectedWorkflowStatuses}
          disabled={!selectedWorkflowId}
          input={<OutlinedInput label="Workflow Status" />}
          renderValue={(selected) =>
            isAllWorkflowStatusesSelected || selected.length === 0 ? (
              <div>All</div>
            ) : (
              <div>
                {selected
                  .map(
                    (value) =>
                      WORKFLOW_STATUS_OPTIONS.find(
                        (option) => option.value === value,
                      )?.label ?? value,
                  )
                  .join(", ")}
              </div>
            )
          }
          MenuProps={MenuProps}
        >
          <MenuItem
            value="all"
            onClick={(e) => {
              e.preventDefault();
              toggleMultiSelect(
                "all",
                setSelectedWorkflowStatuses,
                WORKFLOW_STATUS_VALUES,
              );
            }}
          >
            <Checkbox
              checked={isAllWorkflowStatusesSelected}
              indeterminate={
                selectedWorkflowStatuses.length > 0 &&
                !isAllWorkflowStatusesSelected
              }
            />
            <ListItemText primary="All" />
          </MenuItem>
          {WORKFLOW_STATUS_OPTIONS.map((status) => (
            <MenuItem
              key={status.value}
              value={status.value}
              onClick={(e) => {
                e.preventDefault();
                toggleMultiSelect(
                  status.value,
                  setSelectedWorkflowStatuses,
                  WORKFLOW_STATUS_VALUES,
                );
              }}
            >
              <Checkbox
                checked={selectedWorkflowStatuses.includes(status.value)}
              />
              <ListItemText primary={status.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  return (
    <TicketSlReportShell
      title="Workflow SL"
      downloadFileName="Ticket_Workflow_SL_Report"
      errorMessage="Failed to fetch Workflow SL report"
      reportData={reportData}
      reportLoading={reportLoading}
      reportFetching={reportFetching}
      isError={isError}
      error={error}
      refetch={refetch}
      params={params}
      setParams={setParams}
      buildExtraParams={buildExtraParams}
      extraFilters={extraFilters}
      onResetExtra={() => setSelectedWorkflowStatuses([])}
    />
  );
};

export default TicketWorkflowSlReport;
